import React, { useEffect, useRef, useState } from "react";
import {  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Divider,
  Stack,
  alpha,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Snackbar,
  Alert,
  MenuItem,
  Select,} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Save, Edit, Delete, Add, Waves, Speed } from "@mui/icons-material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../Contexts/SettingsContext";
import { getSelectedTrip } from "../../utils/voyageSelection";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";

const LoadingLogTable = ({
  lastAvgRate,
  avgRate,
  setAvgRate,
  setLastAvgRate,
  etcCurrent,
  etcAvg,
  setEtcAvg,
  setEtcCurrent,
}) => {
  const { t } = useTranslation("logging");
  const theme = useTheme();
  const { is24Hour } = useSettings();
  const { tanks: cargoTanks } = useShipSettings();
  const {
    selectedTripId,
    selectedCargoIndex,
    currentVoyage,
    isVoyageReadOnly,
    updateTripCargo,
  } = useVoyageContextV2();

  const [logEntries, setLogEntries] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    index: null,
  });
  const entryRefs = useRef({});

  // --- DATA SYNC ---
  useEffect(() => {
    const trip = getSelectedTrip(currentVoyage, selectedTripId);
    const selectedCargo = trip?.cargos?.[selectedCargoIndex];
    if (selectedCargo?.loadingLog) setLogEntries(selectedCargo.loadingLog);
  }, [currentVoyage, selectedTripId, selectedCargoIndex]);

  useEffect(() => {
    if (editIndex == null) return;

    const targetEntry = entryRefs.current[editIndex];
    if (!targetEntry) return;

    targetEntry.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [editIndex, logEntries.length]);

  // --- BERÄKNINGAR ---
  const calculateCurrentRate = (entries, index) => {
    if (index === 0) return 0;
    const prev = entries[index - 1];
    const curr = entries[index];
    const timeDiffHours =
      (new Date(curr.localTime) - new Date(prev.localTime)) / (1000 * 3600);
    if (timeDiffHours <= 0) return 0;

    // Vid lastning: Nuvarande OBQ minus Föregående OBQ
    const volDiff = (parseFloat(curr.obq) || 0) - (parseFloat(prev.obq) || 0);
    return parseFloat(volDiff / timeDiffHours).toFixed(1);
  };

  const calculateTotalRatesAndEtc = (entries) => {
    if (entries.length < 2) return;

    const trip = getSelectedTrip(currentVoyage, selectedTripId);
    const totalQuantity = trip?.cargos?.[selectedCargoIndex]?.quantity || 0;

    let totalVol = 0;
    let totalTime = 0;
    let lastRate = 0;

    for (let i = 1; i < entries.length; i++) {
      const prev = entries[i - 1];
      const curr = entries[i];
      const volDiff = (parseFloat(curr.obq) || 0) - (parseFloat(prev.obq) || 0);
      const timeDiff =
        (new Date(curr.localTime) - new Date(prev.localTime)) / (1000 * 3600);

      if (timeDiff > 0) {
        totalVol += volDiff;
        totalTime += timeDiff;
        lastRate = parseFloat((volDiff / timeDiff).toFixed(1));
      }
    }

    const calculatedAvg = totalTime > 0 ? totalVol / totalTime : 0;
    setAvgRate(calculatedAvg.toFixed(1));
    setLastAvgRate(lastRate);

    // ETC Beräkning
    const lastEntry = entries[entries.length - 1];
    const currentObq = parseFloat(lastEntry?.obq) || 0;
    const remaining = totalQuantity - currentObq;
    const lastTime = new Date(lastEntry?.localTime).getTime();

    if (remaining > 0 && lastTime) {
      if (calculatedAvg > 0) {
        setEtcAvg(new Date(lastTime + (remaining / calculatedAvg) * 3600000));
      }
      if (lastRate > 0) {
        setEtcCurrent(new Date(lastTime + (remaining / lastRate) * 3600000));
      }
    }
  };

  // --- HANDLERS ---
  const handleInputChange = (index, field, value) => {
    if (isVoyageReadOnly) return;
    const updated = [...logEntries];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "obq" || field === "localTime") {
      updated[index].rate = calculateCurrentRate(updated, index);
    }
    setLogEntries(updated);
  };

  const handleSave = async (index) => {
    if (isVoyageReadOnly) return;
    try {
      const trip = getSelectedTrip(currentVoyage, selectedTripId);
      const selectedCargo = trip?.cargos?.[selectedCargoIndex];
      if (!selectedCargo?.cargoId) return;

      await updateTripCargo(
        selectedCargo.cargoId,
        {
        loadingLog: logEntries,
        loadingLogRates: { avgRate, lastAvgRate, etcAvg, etcCurrent },
        },
        { summary: "Updated loading log" }
      );
      calculateTotalRatesAndEtc(logEntries);
      setEditIndex(null);
      setSnackbar({
        open: true,
        message: t("savedSuccess"),
        severity: "success",
      });
    } catch (e) {
      setSnackbar({ open: true, message: "Error saving", severity: "error" });
    }
  };

  return (
    <Box sx={{ mt: 3, pb: 10 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        mb={3}
      >
        <Typography
          variant="h5"
          fontWeight="900"
          sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
        >
          <Box
            sx={{
              p: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 2,
              display: "flex",
            }}
          >
            <Waves color="primary" />
          </Box>
          {t("loadingLog")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          disabled={isVoyageReadOnly}
          onClick={() => {
            const now = dayjs().format("YYYY-MM-DDTHH:mm");
            const last = logEntries[logEntries.length - 1];
            setLogEntries([
              ...logEntries,
              {
                localTime: now,
                obq: last?.obq || "",
                rate: 0,
                tanksNo: last?.tanksNo || [],
                manifPress: "",
                sf: last?.sf || "",
                bm: last?.bm || "",
                remarks: "No Remarks",
              },
            ]);
            setEditIndex(logEntries.length);
          }}
          sx={{
            borderRadius: 3,
            px: 3,
            fontWeight: 700,
            alignSelf: { xs: "flex-start", sm: "auto" },
          }}
        >
          {t("addEvent")}
        </Button>
      </Box>

      {/* Timeline entries */}
      {logEntries.map((entry, index) => {
        const isEditing = editIndex === index;
        return (
          <Paper
            key={index}
            ref={(node) => {
              if (node) {
                entryRefs.current[index] = node;
              } else {
                delete entryRefs.current[index];
              }
            }}
            elevation={isEditing ? 6 : 0}
            sx={{
              p: 2.5,
              mb: 2.5,
              borderRadius: 4,
              border: "1px solid",
              borderColor: isEditing ? "primary.main" : "divider",
              background: isEditing
                ? `linear-gradient(to bottom right, #ffffff, ${alpha(
                    theme.palette.primary.main,
                    0.02
                  )})`
                : theme.palette.background.paper,
              transition: "all 0.2s ease-in-out",
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid size={{ xs: 12, lg: 4 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                >
                  <DatePicker
                    disabled={!isEditing || isVoyageReadOnly}
                    value={dayjs(entry.localTime)}
                    onChange={(val) =>
                      handleInputChange(
                        index,
                        "localTime",
                        val.format("YYYY-MM-DD") +
                          "T" +
                          dayjs(entry.localTime).format("HH:mm")
                      )
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { bgcolor: "background.paper" },
                      },
                    }}
                  />
                  <TimePicker
                    disabled={!isEditing || isVoyageReadOnly}
                    value={dayjs(entry.localTime)}
                    onChange={(val) =>
                      handleInputChange(
                        index,
                        "localTime",
                        dayjs(entry.localTime).format("YYYY-MM-DD") +
                          "T" +
                          val.format("HH:mm")
                      )
                    }
                    ampm={!is24Hour}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { bgcolor: "background.paper" },
                      },
                    }}
                  />
                </Stack>
              </Grid>

              <Grid size={{ xs: 6, sm: 3, lg: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Speed sx={{ color: "text.secondary", fontSize: 20 }} />
                  <Box>
                    <Typography
                      variant="caption"
                      fontWeight="800"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase" }}
                    >
                      Rate
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="900"
                      color="primary.main"
                    >
                      {entry.rate}{" "}
                      <small style={{ fontSize: "0.7rem" }}>m³/h</small>
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 6, sm: 3, lg: 2 }}>
                <TextField
                  fullWidth
                  label={t("obqm3")}
                  size="small"
                  type="number"
                  value={entry.obq}
                  disabled={!isEditing || isVoyageReadOnly}
                  onChange={(e) =>
                    handleInputChange(index, "obq", e.target.value)
                  }
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Grid>

              <Grid size={{ xs: 12, lg: 4 }}>
                <Typography
                  variant="caption"
                  fontWeight="800"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block", textTransform: "uppercase" }}
                >
                  {t("cargoTanks")}
                </Typography>
                {cargoTanks.length > 0 ? (
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {cargoTanks.map((tank) => (
                      <Button
                        key={tank.id}
                        size="small"
                        variant={
                          entry.tanksNo?.includes(tank.name)
                            ? "contained"
                            : "outlined"
                        }
                        disabled={!isEditing || isVoyageReadOnly}
                        onClick={() => {
                          const next = entry.tanksNo?.includes(tank.name)
                            ? entry.tanksNo.filter((t) => t !== tank.name)
                            : [...(entry.tanksNo || []), tank.name];
                          handleInputChange(index, "tanksNo", next);
                        }}
                        sx={{
                          minWidth: 42,
                          height: 26,
                          fontSize: "0.65rem",
                          borderRadius: 1.5,
                          flexShrink: 0,
                        }}
                      >
                        {tank.name}
                      </Button>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("noCargoTanksConfigured")}
                  </Typography>
                )}
              </Grid>

              <Grid size={12}>
                <Divider sx={{ borderStyle: "dashed", opacity: 0.5 }} />
              </Grid>

              <Grid container spacing={2} alignItems="center" size={12}>
                <Grid size={{ xs: 12, sm: 4, md: 2, lg: 1.5 }}>
                  <TextField
                    fullWidth
                    label="Press"
                    size="small"
                    value={entry.manifPress}
                    disabled={!isEditing || isVoyageReadOnly}
                    onChange={(e) =>
                      handleInputChange(index, "manifPress", e.target.value)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 2, lg: 1.5 }}>
                  <TextField
                    fullWidth
                    label="SF"
                    size="small"
                    type="number"
                    value={entry.sf}
                    disabled={!isEditing || isVoyageReadOnly}
                    onChange={(e) =>
                      handleInputChange(index, "sf", e.target.value)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 4, md: 2, lg: 1.5 }}>
                  <TextField
                    fullWidth
                    label="BM"
                    size="small"
                    type="number"
                    value={entry.bm}
                    disabled={!isEditing || isVoyageReadOnly}
                    onChange={(e) =>
                      handleInputChange(index, "bm", e.target.value)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 5.5 }}>
                  <Select
                    fullWidth
                    size="small"
                    value={entry.remarks || "No Remarks"}
                    disabled={!isEditing || isVoyageReadOnly}
                    onChange={(e) =>
                      handleInputChange(index, "remarks", e.target.value)
                    }
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="No Remarks">
                      {t("loadingRemarks.noRemarks")}
                    </MenuItem>
                    <MenuItem value="Start Loading">
                      {t("loadingRemarks.startLoading")}
                    </MenuItem>
                    <MenuItem value="Resume Loading">
                      {t("loadingRemarks.resumeLoading")}
                    </MenuItem>
                    <MenuItem value="Shore Stop">
                      {t("loadingRemarks.shoreStop")}
                    </MenuItem>
                    <MenuItem value="Ship Stop">
                      {t("loadingRemarks.shipStop")}
                    </MenuItem>
                    <MenuItem value="Loading Completed">
                      {t("loadingRemarks.loadingCompleted")}
                    </MenuItem>
                  </Select>
                </Grid>
                <Grid
                  display="flex"
                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  size={{ xs: 12, md: 12, lg: 2 }}
                >
                  <Stack direction="row" spacing={1}>
                    {isEditing ? (
                      <IconButton
                        color="success"
                        disabled={isVoyageReadOnly}
                        onClick={() => handleSave(index)}
                        sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}
                      >
                        <Save />
                      </IconButton>
                    ) : (
                      <IconButton
                        color="primary"
                        disabled={isVoyageReadOnly}
                        onClick={() => setEditIndex(index)}
                        sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                      >
                        <Edit />
                      </IconButton>
                    )}
                    <IconButton
                      color="error"
                      disabled={isVoyageReadOnly}
                      onClick={() => setDeleteDialog({ open: true, index })}
                      sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, index: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>{t("confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("confirmDelete")}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteDialog({ open: false, index: null })}>
            {t("cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
            disabled={isVoyageReadOnly}
            onClick={async () => {
              if (isVoyageReadOnly) return;
              const updated = logEntries.filter(
                (_, i) => i !== deleteDialog.index
              );
              const trip = getSelectedTrip(currentVoyage, selectedTripId);
              const selectedCargo = trip?.cargos?.[selectedCargoIndex];
              if (!selectedCargo?.cargoId) return;
              await updateTripCargo(
                selectedCargo.cargoId,
                { loadingLog: updated },
                { summary: "Deleted loading log entry" }
              );
              setDeleteDialog({ open: false, index: null });
            }}
          >
            {t("removeEvent")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoadingLogTable;
